import { IGatewayForm } from "../../models";

/**
 * Sends the browser to the bank's payment page with a full-page form POST.
 * The bank forbids showing its page inside an iframe (EPM rules 2.6).
 */
export const submitGatewayForm = (form: IGatewayForm) => {
  const el = document.createElement("form");
  el.method = "POST";
  el.action = form.action;
  el.acceptCharset = "UTF-8";
  el.style.display = "none";

  Object.keys(form.fields).forEach((name) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = form.fields[name] ?? "";
    el.appendChild(input);
  });

  document.body.appendChild(el);
  el.submit();
};

const PENDING_KEY = "bpb.pendingPaymentToken";

/** Remembers which order this browser sent to the bank, so its receipt can clear the cart. */
export const setPendingToken = (token: string) => {
  try {
    localStorage.setItem(PENDING_KEY, token);
  } catch {
    /* private mode: the cart just stays */
  }
};

export const isPendingToken = (token: string) => {
  try {
    return localStorage.getItem(PENDING_KEY) === token;
  } catch {
    return false;
  }
};

export const clearPendingToken = () => {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
};

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

let recaptchaLoading: Promise<void> | null = null;

/** Loads Google reCAPTCHA v3 once, only when card payment is actually used. */
export const loadRecaptcha = (siteKey: string): Promise<void> => {
  if (!siteKey) return Promise.resolve();
  if (!recaptchaLoading) {
    recaptchaLoading = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        recaptchaLoading = null;
        reject(new Error("reCAPTCHA failed to load"));
      };
      document.head.appendChild(script);
    });
  }
  return recaptchaLoading;
};

/** Token for the server check. Empty string when reCAPTCHA isn't configured or can't load. */
export const getRecaptchaToken = async (siteKey: string): Promise<string> => {
  if (!siteKey) return "";
  try {
    await loadRecaptcha(siteKey);
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) return "";
    await new Promise<void>((resolve) => grecaptcha.ready(resolve));
    return await grecaptcha.execute(siteKey, { action: "checkout" });
  } catch {
    return "";
  }
};
